import React from 'react';
import {
  PROJECT_STATUS_BADGE_CLASS,
  PROJECT_STATUS_LABELS,
  PROJECT_PRIORITY_BADGE_CLASS,
  PROJECT_PRIORITY_LABELS,
  projectCode,
  formatDate,
  userDisplayName,
  ESCALADE_DIRECTION_JOURS,
  PROJECT_TYPE_BADGE_CLASS,
  libelleType,
  libelleService,
} from './helpers';

/**
 * Carte projet commune au tableau de bord et au portefeuille.
 *
 * Ce qu'un directeur cherche en balayant la grille : qui pilote, pour quand,
 * et s'il y a le feu (retards, blocages). L'« objectif » qui répétait le titre
 * n'est plus affiché ; la description ne l'est que si elle apporte autre chose
 * que le nom du projet.
 */
function ProjectCard({ project, users = [], overdueCount = 0, onSelect, showPriority = false, showBudget = true }) {
  const manager = users.find((u) => u.id === project.project_manager_id);
  const echeance = project.end_date ? new Date(project.end_date) : null;
  const aujourdhui = new Date();
  aujourdhui.setHours(0, 0, 0, 0);
  const echeanceDepassee =
    echeance && echeance < aujourdhui && project.status !== 'Completed' && project.status !== 'Cancelled';

  const description = (project.description || '').trim();
  const nom = (project.name || '').trim().toLowerCase();
  const descriptionUtile =
    description &&
    description.toLowerCase().replace(/^objectif\s*:\s*/i, '').replace(/\.$/, '') !== nom.replace(/\.$/, '');

  const budgetRenseigne = project.budget_allocated > 0;

  return (
    <button
      type="button"
      className="project-card"
      onClick={() => onSelect(project)}
      aria-label={`Ouvrir le projet ${projectCode(project.id)} ${project.name}`}
    >
      <div className="project-card-top">
        <span className="project-ref">{projectCode(project.id)}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {showPriority && project.priority && (
            <span
              className={`badge ${PROJECT_PRIORITY_BADGE_CLASS[project.priority] || 'badge-todo'}`}
              title={PROJECT_PRIORITY_LABELS[project.priority] || project.priority}
            >
              {project.priority}
            </span>
          )}
          {project.charte_etat && project.charte_etat !== 'validee' && (
            <span className="badge badge-todo" title="La charte n'est pas encore validée">
              {project.charte_etat === 'brouillon' ? 'Charte à faire' : 'En validation'}
            </span>
          )}
          <span className={`badge ${PROJECT_STATUS_BADGE_CLASS[project.status] || 'badge-active'}`}>
            {PROJECT_STATUS_LABELS[project.status] || project.status}
          </span>
        </div>
      </div>

      <div className="project-title">{project.name}</div>
      {(project.project_type || project.project_service) && (
        <div className="project-etiquettes">
          {project.project_type && (
            <span className={`badge ${PROJECT_TYPE_BADGE_CLASS[project.project_type] || 'badge-todo'}`}>
              {libelleType(project.project_type)}
            </span>
          )}
          {project.project_service && (
            <span className="badge badge-service">{libelleService(project.project_service)}</span>
          )}
        </div>
      )}

      {project.blocked_count > 0 && (
        <div
          className={`blocage-ligne blocage-ligne-${
            project.blocked_alert
              ? project.blocked_days_max >= ESCALADE_DIRECTION_JOURS
                ? 'direction'
                : 'service'
              : 'normal'
          }`}
        >
          {project.blocked_count} tâche{project.blocked_count > 1 ? 's' : ''} bloquée
          {project.blocked_count > 1 ? 's' : ''} — la plus ancienne depuis {project.blocked_days_max} j
        </div>
      )}

      {/* Description repliée sur trois lignes ; elle se déplie au survol de la
          carte (ou au focus clavier), pour garder une grille régulière. */}
      {descriptionUtile && <div className="project-meta project-description">{description}</div>}

      <div className="project-faits">
        <div>
          <div className="project-fait-label">Chef de projet</div>
          <div className="project-fait-valeur">{manager ? userDisplayName(manager) : <span className="valeur-absente" style={{ fontSize: 12 }}>À désigner</span>}</div>
        </div>
        <div>
          <div className="project-fait-label">Échéance</div>
          <div className={`project-fait-valeur ${echeanceDepassee ? 'retard' : ''}`}>
            {project.end_date ? formatDate(project.end_date) : <span className="valeur-absente" style={{ fontSize: 12 }}>Non fixée</span>}
            {echeanceDepassee && ' · dépassée'}
          </div>
        </div>
        <div>
          <div className="project-fait-label">Tâches</div>
          <div className="project-fait-valeur">
            {project.task_count ? `${project.task_done_count || 0} / ${project.task_count} terminées` : <span className="valeur-absente" style={{ fontSize: 12 }}>Aucune</span>}
          </div>
        </div>
        <div>
          <div className="project-fait-label">En retard</div>
          <div className={`project-fait-valeur ${overdueCount > 0 ? 'retard' : ''}`}>
            {overdueCount > 0 ? `${overdueCount} tâche${overdueCount > 1 ? 's' : ''}` : '—'}
          </div>
        </div>
      </div>

      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${project.progress_percent || 0}%` }} />
      </div>
      <div className="project-card-footer">
        <span>Tâches terminées</span>
        <span>{project.progress_percent || 0}%</span>
      </div>
      {project.milestone_count > 0 && (
        <div className="project-card-footer">
          <span>Jalons atteints</span>
          <span>{project.milestone_done_count}/{project.milestone_count}</span>
        </div>
      )}

      {showBudget && budgetRenseigne && (
        <>
          <div className="progress-bar" style={{ marginTop: 4 }}>
            <div
              className="progress-fill"
              style={{ width: `${Math.min(100, project.budget_consumed_percent || 0)}%`, background: '#C19E54' }}
            />
          </div>
          <div className="project-card-footer">
            <span>Budget consommé</span>
            <span>{project.budget_consumed_percent || 0}%</span>
          </div>
        </>
      )}
    </button>
  );
}

export default ProjectCard;
